import { FunctionComponent, MouseEvent, useState } from "react";
import { useTranslation } from "react-i18next";

import { faPaperPlane } from "@fortawesome/pro-regular-svg-icons";
import { faBolt, faCheck, faSpinner } from "@fortawesome/pro-solid-svg-icons";

import { FeedPerson, FeedRunCard, FeedSignal } from "@signalist/shared-types";

import { Icon } from "@/components";
import { classNames } from "@/utils/classNames";
import { useAppSelector } from "@/redux/hooks";

import { selectJobByProspectId } from "@/pages/Private/redux/copywritingOrchestration/copywritingOrchestration.slice";

import { feedCardKey } from "../../utils/feedCardKey";
import { useFeedCardGeneration } from "../../utils/useFeedCardGeneration";
import { useFeedReviewNow } from "../../utils/useFeedReviewNow";
import { GenerationConflictDialog } from "./GenerationConflictDialog";
import { SignalGeneratePopover } from "./SignalGeneratePopover";

// Display-agnostic chip base — the display utility is added per state so the
// always-visible job chips (`inline-flex`) and the hover-gated idle CTA
// (`hidden` → `group-hover/sigrow:inline-flex`) don't fight over `display`.
const ROW_CHIP_BASE =
	"shrink-0 items-center gap-1.5 rounded-md border px-2 py-1 text-[11px] font-semibold shadow-sm";
const ROW_CHIP = `inline-flex ${ROW_CHIP_BASE}`;
const EXPAND_CHIP = "flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold";

interface SignalAiCtaProps {
	card: FeedRunCard;
	signal: FeedSignal;
	/** The card's currently selected person — the generation target (D3/D5). */
	person: FeedPerson | null;
	/** "row" = hover CTA in the collapsed one-liner; "expand" = primary CTA in the detail row. */
	variant: "row" | "expand";
}

/**
 * Per-signal-row AI CTA (PREC-3635 P6): idle "Let AI …" trigger into the
 * signal-seeded shared GenerateBar panel ({@link SignalGeneratePopover}), the
 * generating/queued/ready mirror of the person's job — shown only when the
 * job's `seedSignalId` matches this row — and the D5 conflict prompts for a
 * job in flight/ready from another signal.
 */
export const SignalAiCta: FunctionComponent<SignalAiCtaProps> = ({
	card,
	signal,
	person,
	variant,
}) => {
	const { t } = useTranslation();
	const job = useAppSelector((state) => selectJobByProspectId(state, person?.prospectId));
	const { generateForPerson, renderFeedGenerationSnackbar } = useFeedCardGeneration();
	const { reviewJobNow } = useFeedReviewNow();

	const [panelAnchorRect, setPanelAnchorRect] = useState<DOMRect | null>(null);
	const [conflictVariant, setConflictVariant] = useState<"in-flight" | "ready" | null>(null);

	// Same degraded-card rule as the card button (D3, P5 D7).
	if (!person?.prospectId || !person.listPersonId) {
		return null;
	}

	const firstName = (person.name ?? "").split(" ")[0] ?? "";
	const seedText = signal.line ?? signal.title ?? "";
	// The row only ever mirrors the job it seeded (mockup `mine`, D2/§6.2).
	const isRowJob = !!job && job.seedSignalId === signal.signalId;
	const chipClass = variant === "row" ? ROW_CHIP : EXPAND_CHIP;

	const stop = (handler: (event: MouseEvent) => void) => (event: MouseEvent) => {
		event.stopPropagation();
		handler(event);
	};

	const generateSeeded = (userInstruction: string) =>
		void generateForPerson({ card, person, seedSignalId: signal.signalId, userInstruction });

	/** Pre-flight conflict check against the person's job map entry (D5). */
	const handleIdleCta = (event: MouseEvent) => {
		if (job && (job.status === "QUEUED" || job.status === "RUNNING")) {
			setConflictVariant("in-flight");

			return;
		}

		if (job && job.status === "COMPLETED" && !job.reviewedAt) {
			setConflictVariant("ready");

			return;
		}

		setPanelAnchorRect((event.currentTarget as HTMLElement).getBoundingClientRect());
	};

	const renderState = () => {
		if (!isRowJob || !job) {
			return null;
		}

		if (job.status === "RUNNING") {
			return (
				<span
					className={`${chipClass} border-brand-light bg-light-blue text-brand-light`}
					data-testid={`feed-signal-ai-generating-${signal.signalId}`}
				>
					<Icon className="h-3 w-3 animate-spin" icon={faSpinner} />
					{t("feed.signalCta.generating")}
				</span>
			);
		}

		if (job.status === "QUEUED") {
			return (
				<span
					className={`${chipClass} border-border-light bg-background-light text-text-tertiary`}
					data-testid={`feed-signal-ai-queued-${signal.signalId}`}
				>
					<span className="h-1.5 w-1.5 animate-pulse rounded-full bg-text-tertiary" />
					{t("feed.signalCta.queued")}
				</span>
			);
		}

		if (!job.reviewedAt) {
			return (
				<button
					className={`${chipClass} cursor-pointer border-transparent bg-background-success text-text-success hover:brightness-95`}
					data-testid={`feed-signal-ai-ready-${signal.signalId}`}
					type="button"
					onClick={stop(() =>
						reviewJobNow(job, {
							runId: feedCardKey(card),
							listPersonId: person.listPersonId,
						})
					)}
				>
					<Icon className="h-3 w-3" icon={faCheck} />
					{t("feed.signalCta.ready")}
				</button>
			);
		}

		return null;
	};

	const state = renderState();

	return (
		<>
			{state ??
				(variant === "row" ? (
					<button
						className={classNames(
							ROW_CHIP_BASE,
							// Hover-revealed like the mockup's `.sig-ref` — resting rows stay calm.
							// Uses the display-agnostic base so `hidden` isn't overridden by a
							// baked-in `inline-flex`; the group-hover variant flips it on hover.
							"hidden cursor-pointer border-brand-light bg-light-blue text-brand-light group-hover/sigrow:inline-flex"
						)}
						data-testid={`feed-signal-ai-cta-${signal.signalId}`}
						type="button"
						onClick={stop(handleIdleCta)}
					>
						<Icon className="h-3 w-3" icon={faPaperPlane} />
						{t("feed.signalCta.mention")}
					</button>
				) : (
					<button
						className={`${EXPAND_CHIP} cursor-pointer border-transparent bg-blue-action text-white hover:bg-blue-action-dark`}
						data-testid={`feed-signal-ai-cta-${signal.signalId}`}
						type="button"
						onClick={stop(handleIdleCta)}
					>
						<Icon className="h-3 w-3" icon={faBolt} />
						{t("feed.signalCta.draft")}
					</button>
				))}

			{/* The shared GenerateBar panel, seeded by this signal (mounted on demand). */}
			{panelAnchorRect && (
				<SignalGeneratePopover
					anchorRect={panelAnchorRect}
					person={person}
					seedSignalId={signal.signalId}
					seedText={seedText}
					onClose={() => setPanelAnchorRect(null)}
				/>
			)}

			{conflictVariant && (
				<GenerationConflictDialog
					personFirstName={firstName}
					variant={conflictVariant}
					onClose={() => setConflictVariant(null)}
					onReplace={() => {
						setConflictVariant(null);
						generateSeeded(seedText);
					}}
				/>
			)}

			{renderFeedGenerationSnackbar()}
		</>
	);
};
